import React, { useEffect, useState } from "react";
import { 
  XMarkIcon, 
  LockClosedIcon, 
  ShieldCheckIcon, 
  SparklesIcon, 
  GiftIcon, 
  CurrencyRupeeIcon,
  CheckBadgeIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ClockIcon,
  BanknotesIcon,
  ArrowPathIcon,
  PhoneIcon,
  EnvelopeIcon,
  StarIcon,
  HandRaisedIcon,
  BuildingLibraryIcon,
  ShieldExclamationIcon
} from "@heroicons/react/24/outline";

import axios from "axios";
import OAuth from "oauth-1.0a";
import CryptoJS from "crypto-js";

const CONSUMER_KEY = process.env.REACT_APP_CONSUMER_KEY;
const CONSUMER_SECRET = process.env.REACT_APP_CONSUMER_SECRET;

// OAuth Setup
const oauth = new OAuth({
  consumer: {
    key: CONSUMER_KEY,
    secret: CONSUMER_SECRET,
  },
  signature_method: "HMAC-SHA1",
  hash_function(base_string, key) {
    return CryptoJS.HmacSHA1(base_string, key).toString(CryptoJS.enc.Base64);
  },
});

// Axios instance (OAuth enabled)
const oauthApi = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

// Apply OAuth only for requests using oauthApi
oauthApi.interceptors.request.use((config) => {
  const requestData = {
    url: config.url,
    method: config.method.toUpperCase(),
  };

  const authHeader = oauth.toHeader(oauth.authorize(requestData));

  config.headers = {
    ...config.headers,
    ...authHeader,
  };

  return config;
});

const DonationFormModal = ({
  onClose,
  campaign,
  selectedCurrency,
  selectedPaymentMethod,
  formatCurrency,
  showDonationConfetti,
  setShowDonationConfetti,

  loggedInUser,
}) => {
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [mobileError, setMobileError] = useState("");

  const [isAnonymous, setIsAnonymous] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  const tipOptions = [0, 10, 18, 30, 40, 50];
  const [tipPercentage, setTipPercentage] = useState(18);

  // Prefill fields when loggedInUser changes
  useEffect(() => {
    if (loggedInUser) {
      setName(loggedInUser?.name || "");
      setEmail(loggedInUser?.email || "");
      setMobile(loggedInUser?.mobile || "");
    }
  }, [loggedInUser]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  // Validation functions
  const validateName = (value) => {
    if (!isAnonymous && !value.trim()) {
      return "Name is required";
    }
    // Allow letters, spaces, dots, hyphens, and apostrophes
    const nameRegex = /^[a-zA-Z\s\.\-']+$/;
    if (!isAnonymous && value.trim() && !nameRegex.test(value)) {
      return "Name can only contain letters and spaces";
    }
    return "";
  };

  const validateEmail = (value) => {
    if (isAnonymous && !value) {
      return ""; // Email not required for anonymous donations
    }
    if (!value) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validateMobile = (value) => {
    if (isAnonymous && !value) {
      return ""; // Mobile not required for anonymous donations
    }
    if (!value) {
      return "Mobile number is required";
    }
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(value)) {
      return "Please enter a valid 10-digit mobile number";
    }
    return "";
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    setNameError(validateName(value));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
    setMobile(value);
    setMobileError(validateMobile(value));
  };

  const handleAnonymousChange = (e) => {
    const anonymous = e.target.checked;
    setIsAnonymous(anonymous);
    
    if (anonymous) {
      // Clear errors when anonymous is checked
      setNameError("");
      setEmailError("");
      setMobileError("");
    } else {
      // Re-validate all fields when unchecked
      setNameError(validateName(name));
      setEmailError(validateEmail(email));
      setMobileError(validateMobile(mobile));
    }
  };

  const formCampaign = campaign || {
    title:
      "My Father Is Fighting For His Life And We Need Your Support To Save Him",
    campaigner: campaign?.creator_name || "Jyotsna Venkatamathi P",
    beneficiary: campaign?.patient_name || "Mr Padmanabhan V",
    supporters: campaign?.supporters || 85,
    goal: campaign?.goal_amount || 500000,
    daysLeft: campaign?.days_left || 26,
  };

  const presetAmounts = [300, 1000, 3000];
  const tipAmount = Math.round(amount * (tipPercentage / 100));
  const totalAmount = amount + tipAmount;

  const handleAmountSelect = (amt) => {
    setAmount(amt);
    setCustomAmount("");
  };

  const handleCustomAmount = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCustomAmount(value);
    if (value) {
      setAmount(parseInt(value) || 0);
    }
  };

  // Load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById("razorpay-script")) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.id = "razorpay-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  const validateForm = () => {
    const nameValidation = validateName(name);
    const emailValidation = validateEmail(email);
    const mobileValidation = validateMobile(mobile);

    setNameError(nameValidation);
    setEmailError(emailValidation);
    setMobileError(mobileValidation);

    return !nameValidation && !emailValidation && !mobileValidation;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // API 1: Create Donation
    const DONATION_API_URL = "https://api.donatepe.org/donations/create";

    // API 2: Create Razorpay Order (using donationId)
    const CREATE_ORDER_API_URL = "https://api.donatepe.org/donations/create-order";

    const requestBody = {
      fundraiserId: campaign?.id,
      donorName: isAnonymous ? "Anonymous" : name,
      donorEmail: isAnonymous ? "" : email, // Send empty email for anonymous
      donorMobile: isAnonymous ? "" : mobile, // Send empty mobile for anonymous
      amount: amount,
      tipAmount: tipAmount,
      isAnonymous: isAnonymous,
    };

    try {
      // ----------------------------
      // 1️⃣ API CALL: Create Donation
      // ----------------------------
      const donationResponse = await oauthApi.post(DONATION_API_URL, requestBody);

      const donationId = donationResponse.data?.donationId;

      if (!donationId) {
        alert("Donation created but donationId not received.");
        return;
      }

      // ----------------------------
      // 2️⃣ API CALL: Create Razorpay Order
      // ----------------------------
      const orderRequestBody = {
        donationId: donationId,
      };

      const orderResponse = await oauthApi.post(
        CREATE_ORDER_API_URL,
        orderRequestBody
      );

      if (!orderResponse.data?.success) {
        alert("Failed to create Razorpay order.");
        return;
      }

      const { orderId, amount, currency } = orderResponse.data;

      // ----------------------------
      // 3️⃣ LOAD RAZORPAY SCRIPT
      // ----------------------------
      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded) {
        alert("Razorpay SDK failed to load. Check your internet connection.");
        return;
      }

      // ----------------------------
      // 4️⃣ LAUNCH RAZORPAY CHECKOUT
      // ----------------------------
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: "DonatePe",
        description: `Donation for ${formCampaign.title.substring(0, 50)}...`,
        order_id: orderId,
        image: "https://donatepe.org/logo.png",

        handler: async function (response) {
          setShowDonationConfetti(true);

          try {
            const CONFIRM_PAYMENT_API_URL = "https://api.donatepe.org/donations/verify";

            const confirmPaymentBody = {
              donationId: donationId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };

            const confirmResponse = await oauthApi.post(
              CONFIRM_PAYMENT_API_URL,
              confirmPaymentBody
            );

            if (confirmResponse.data?.success !== true && confirmResponse.data?.success !== "true") {
              alert("Payment succeeded but confirmation failed on server.");
              setShowDonationConfetti(false);
              return;
            }

            setShowDonationConfetti(false);
            setShowThankYouModal(true);

          } catch (err) {
            alert(
              "Payment succeeded, but confirming payment on server failed. Please contact support."
            );
            setShowDonationConfetti(false);
          }
        },

        modal: {
          ondismiss: function() {
            setShowCancelModal(true);
          }
        },

        prefill: {
          name: isAnonymous ? "Anonymous" : name,
          email: email,
          contact: mobile,
        },

        theme: {
          color: "#00B7B3",
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response) {
        alert(
          `Payment Failed!\nReason: ${response.error.description || "Unknown"}`
        );
      });

      razorpay.open();
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Something went wrong while processing donation."
      );
    }
  };

  // Cancel Modal Component
  const CancelModal = () => {
    if (!showCancelModal) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
        onClick={() => setShowCancelModal(false)}
      >
        <div
          className="bg-white rounded-3xl shadow-2xl max-w-md w-full relative overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-orange-500"></div>
          
          <div className="relative p-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            
            <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600 mb-2 text-center">
              Payment Cancelled
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Your payment was not completed. No charges have been made.
            </p>
            
            <div className="bg-amber-50 rounded-2xl p-4 mb-6 border border-amber-200">
              <p className="text-sm text-amber-700 text-center">
                Your support means the world to us. Please try again whenever you're ready.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="flex-1 py-3 bg-gradient-to-r from-[#00B7B3] to-[#008F8C] text-white font-semibold rounded-xl hover:shadow-lg transition-all"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Thank You Modal Component
  const ThankYouModal = () => {
    if (!showThankYouModal) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
        onClick={() => setShowThankYouModal(false)}
      >
        <div
          className="bg-white rounded-3xl shadow-2xl max-w-md w-full relative overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00B7B3] to-[#008F8C]"></div>
          
          <div className="relative p-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-[#00B7B3] to-[#008F8C] flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#00B7B3] to-[#008F8C] mb-3 text-center">
              Thank You!
            </h2>
            
            <div className="bg-teal-50 rounded-2xl p-6 mb-6 border border-teal-200">
              <p className="text-gray-700 text-center text-lg mb-3">
                Your generosity is truly appreciated.
              </p>
              <p className="text-gray-600 text-center">
                You've just made a difference in someone's life. Together, we're building a community of hope and compassion.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowThankYouModal(false);
                  onClose();
                }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowThankYouModal(false);
                  onClose();
                  window.location.href = '/';
                }}
                className="flex-1 py-3 bg-gradient-to-r from-[#00B7B3] to-[#008F8C] text-white font-semibold rounded-xl hover:shadow-lg transition-all"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-3xl min-h-screen sm:min-h-0 sm:my-4 flex items-start sm:items-center"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Simple border - hidden on mobile */}
          <div className="hidden sm:block absolute -inset-0.5 bg-gradient-to-r from-[#00B7B3] to-[#008F8C] rounded-3xl opacity-20"></div>
          
          {/* Main card - MOBILE OPTIMIZED with proper scrolling */}
          <div className="relative bg-white w-full sm:rounded-3xl shadow-2xl overflow-y-auto max-h-screen sm:max-h-[90vh]">
            
            {/* Thin gradient bar */}
            <div className="h-1 bg-gradient-to-r from-[#00B7B3] via-[#008F8C] to-[#00B7B3]"></div>
            
            {/* Header - IMPROVED FOR MOBILE with prominent close button */}
            <div className="relative bg-white border-b border-gray-100">
              <div className="relative px-4 sm:px-4 py-3 sm:py-2.5 flex justify-between items-center">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-8 h-8 sm:w-8 sm:h-8 bg-gradient-to-br from-[#00B7B3] to-[#008F8C] rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                    <GiftIcon className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base sm:text-base font-bold text-[#00B7B3] truncate">
                      Support This Campaign
                    </h2>
                    <p className="text-xs sm:text-xs text-gray-500 truncate">
                      Your donation makes a difference
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                  <div className="bg-green-50 text-green-700 text-[10px] sm:text-xs px-2 sm:px-2 py-1 sm:py-1 rounded-full flex items-center gap-1 border border-green-100">
                    <CheckBadgeIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    <span className="inline">Verified</span>
                  </div>
                  {/* Mobile close button */}
                  <button
                    onClick={onClose}
                    className="lg:hidden w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full transition-all flex items-center justify-center shadow-lg border-2 border-white ml-2 flex-shrink-0"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-6 h-6 text-white" />
                  </button>
                  {/* Desktop close button */}
                  <button
                    onClick={onClose}
                    className="hidden lg:flex w-8 h-8 bg-red-50 hover:bg-red-100 rounded-lg transition-all items-center justify-center border border-red-200 ml-1 flex-shrink-0"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-5 h-5 text-red-600" />
                  </button>
                </div>
              </div>
              
              {/* Mobile hint - swipe down to close */}
              <div className="sm:hidden flex justify-center pb-2">
                <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
              </div>
            </div>

            {/* Content - FULLY SCROLLABLE ON MOBILE */}
            <div className="relative px-4 sm:px-4 py-4">
              {/* Mobile: Single column layout, Desktop: 5-column grid */}
              <div className="flex flex-col lg:grid lg:grid-cols-5 gap-5">
                {/* Left column - Campaign & Amount - FULL WIDTH ON MOBILE */}
                <div className="lg:col-span-3 space-y-4">
                  {/* Campaign card - IMPROVED FOR MOBILE */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#00B7B3] to-[#008F8C] rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                        <GiftIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <p className="text-xs font-semibold text-[#00B7B3]">MAKING A DIFFERENCE</p>
                          <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <CheckBadgeIcon className="w-2.5 h-2.5" />
                            <span>Verified</span>
                          </span>
                        </div>
                        <p className="text-gray-800 font-semibold text-sm leading-snug mb-2">
                          {formCampaign.title}
                        </p>
                        <div className="flex items-center gap-2 text-xs flex-wrap">
                          <span className="text-gray-500">by {formCampaign.campaigner}</span>
                          <span className="w-1 h-1 bg-[#00B7B3] rounded-full"></span>
                          <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Active</span>
                        </div>
                        
                        {/* Progress bar */}
                        {/* <div className="mt-3">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-600">₹{formCampaign.supporters * 1000} raised</span>
                            <span className="text-gray-800 font-medium">of ₹{formCampaign.goal}</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#00B7B3] to-[#008F8C] rounded-full"
                              style={{ width: `${Math.min((formCampaign.supporters * 1000 / formCampaign.goal) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                            <span className="flex items-center gap-1 text-gray-600">
                              <UserGroupIcon className="w-3.5 h-3.5 text-[#00B7B3]" />
                              {formCampaign.supporters} supporters
                            </span>
                            <span className="flex items-center gap-1 text-gray-600">
                              <ClockIcon className="w-3.5 h-3.5 text-[#00B7B3]" />
                              {formCampaign.daysLeft} days left
                            </span>
                          </div>
                        </div> */}
                      </div>
                    </div>
                  </div>

                  {/* Amount selection */}
                  <div className="bg-white p-4 rounded-xl border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1">
                      <CurrencyRupeeIcon className="w-4 h-4 text-[#00B7B3]" />
                      Choose amount
                    </h3>
                    
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {presetAmounts.map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => handleAmountSelect(amt)}
                          className={`
                            py-3 rounded-lg border text-sm font-bold
                            transition-all
                            ${amount === amt && !customAmount
                              ? "border-[#00B7B3] bg-gradient-to-br from-[#00B7B3] to-[#008F8C] text-white shadow"
                              : "border-gray-200 bg-white text-gray-700 hover:border-[#00B7B3]/50"
                            }
                          `}
                        >
                          ₹{amt}
                        </button>
                      ))}
                    </div>

                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Custom amount"
                        value={customAmount}
                        onChange={handleCustomAmount}
                        className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00B7B3] focus:ring-1 focus:ring-[#00B7B3]/20"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    </div>
                  </div>

                  {/* Tip section */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm text-gray-800">Support DonatePe</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1">
                        <BanknotesIcon className="w-3 h-3" />
                        0% fee
                      </span>
                    </div>
                    
                    <select
                      value={tipPercentage}
                      onChange={(e) => setTipPercentage(Number(e.target.value))}
                      className="w-full px-3 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#00B7B3] focus:ring-1 focus:ring-[#00B7B3]/20 mb-3"
                    >
                      {tipOptions.map((tip) => (
                        <option key={tip} value={tip}>{tip}% tip (₹{Math.round(amount * tip / 100)})</option>
                      ))}
                    </select>

                    {/* Total amount */}
                    <div className="flex justify-end">
                      <div className="bg-gradient-to-r from-[#00B7B3] to-[#008F8C] px-5 py-3 rounded-lg shadow">
                        <p className="text-xs text-white/80">Total donation</p>
                        <p className="text-lg font-bold text-white">₹{totalAmount}</p>
                      </div>
                    </div>
                  </div>

                  {/* Trust badges - HIDDEN ON MOBILE, SHOWN IN FORM SECTION */}
                  <div className="hidden lg:grid grid-cols-3 gap-1.5">
                    <div className="bg-blue-50 p-1.5 rounded-lg text-center">
                      <span className="text-[10px] font-medium text-blue-700">Registered</span>
                    </div>
                    <div className="bg-purple-50 p-1.5 rounded-lg text-center">
                      <span className="text-[10px] font-medium text-purple-700">10L+ lives</span>
                    </div>
                    <div className="bg-amber-50 p-1.5 rounded-lg text-center">
                      <span className="text-[10px] font-medium text-amber-700">4.9/5</span>
                    </div>
                  </div>
                </div>

                {/* Right column - Form */}
                <div className="lg:col-span-2">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1">
                      <ShieldCheckIcon className="w-4 h-4 text-[#00B7B3]" />
                      Your details
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <input
                          type="text"
                          value={name}
                          onChange={handleNameChange}
                          required={!isAnonymous}
                          disabled={isAnonymous}
                          placeholder={isAnonymous ? "Anonymous" : "Full name *"}
                          className={`w-full px-3 py-3 bg-white border rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00B7B3] focus:ring-1 focus:ring-[#00B7B3]/20 ${
                            nameError ? 'border-red-300' : 'border-gray-200'
                          } ${isAnonymous ? 'bg-gray-100 text-gray-500' : ''}`}
                        />
                        {nameError && !isAnonymous && (
                          <p className="text-red-500 text-xs mt-1">{nameError}</p>
                        )}
                      </div>

                      <div className="flex items-center bg-white p-3 rounded-lg border border-gray-200">
                        <input
                          type="checkbox"
                          id="anonymous"
                          checked={isAnonymous}
                          onChange={handleAnonymousChange}
                          className="h-4 w-4 rounded border-gray-300 text-[#00B7B3]"
                        />
                        <label htmlFor="anonymous" className="ml-2 text-sm text-gray-700">
                          Donate anonymously
                        </label>
                      </div>

                      <div>
                        <input
                          type="email"
                          value={email}
                          onChange={handleEmailChange}
                          required={!isAnonymous}
                          disabled={isAnonymous}
                          placeholder={isAnonymous ? "Email (optional)" : "Email *"}
                          className={`w-full px-3 py-3 bg-white border rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00B7B3] focus:ring-1 focus:ring-[#00B7B3]/20 ${
                            emailError ? 'border-red-300' : 'border-gray-200'
                          } ${isAnonymous ? 'bg-gray-100 text-gray-500' : ''}`}
                        />
                        {emailError && !isAnonymous && (
                          <p className="text-red-500 text-xs mt-1">{emailError}</p>
                        )}
                      </div>

                      <div>
                        <input
                          type="tel"
                          value={mobile}
                          onChange={handleMobileChange}
                          required={!isAnonymous}
                          disabled={isAnonymous}
                          placeholder={isAnonymous ? "Mobile (optional)" : "Mobile *"}
                          maxLength="10"
                          className={`w-full px-3 py-3 bg-white border rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00B7B3] focus:ring-1 focus:ring-[#00B7B3]/20 ${
                            mobileError ? 'border-red-300' : 'border-gray-200'
                          } ${isAnonymous ? 'bg-gray-100 text-gray-500' : ''}`}
                        />
                        {mobileError && !isAnonymous && (
                          <p className="text-red-500 text-xs mt-1">{mobileError}</p>
                        )}
                      </div>

                      {/* Security badges */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 text-blue-700">
                            <LockClosedIcon className="w-4 h-4" />
                            <span className="text-sm font-medium">SSL Secure</span>
                          </div>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 text-purple-700">
                            <ShieldCheckIcon className="w-4 h-4" />
                            <span className="text-sm font-medium">Razorpay</span>
                          </div>
                        </div>
                      </div>

                      {/* Trust badges - SHOWN ON MOBILE ONLY */}
                      <div className="grid grid-cols-3 gap-2 lg:hidden">
                        <div className="bg-blue-50 p-2 rounded-lg text-center">
                          <span className="text-xs font-medium text-blue-700">Registered</span>
                        </div>
                        <div className="bg-purple-50 p-2 rounded-lg text-center">
                          <span className="text-xs font-medium text-purple-700">10L+ lives</span>
                        </div>
                        <div className="bg-amber-50 p-2 rounded-lg text-center">
                          <span className="text-xs font-medium text-amber-700">4.9/5</span>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-4 bg-gradient-to-r from-[#00B7B3] to-[#008F8C] text-white font-bold text-base rounded-lg shadow-md hover:shadow-lg transition-all mt-2"
                      >
                        {isAnonymous ? 'Donate Anonymously' : `Donate ₹${totalAmount}`}
                      </button>

                      {/* Payment methods */}
                      <div className="flex justify-center gap-4 pt-2 flex-wrap">
                        <span className="text-xs text-gray-400 font-medium">Visa</span>
                        <span className="text-xs text-gray-400 font-medium">Mastercard</span>
                        <span className="text-xs text-gray-400 font-medium">UPI</span>
                        <span className="text-xs text-gray-400 font-medium">NetBanking</span>
                      </div>

                      
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with cancel button - NOT STICKY ON MOBILE */}
            <div className="bg-gray-50 border-t border-gray-200 px-4 sm:px-4 py-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3 order-2 sm:order-1">
                  <span className="text-gray-500 text-xs">Secure Payment</span>
                  <span className="text-[#00B7B3] text-xs">|</span>
                  <span className="text-gray-500 text-xs">SSL Encrypted</span>
                </div>
                <div className="flex items-center gap-3 order-1 sm:order-2 w-full sm:w-auto">
                  <button 
                    onClick={onClose}
                    className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl text-base shadow-md flex items-center justify-center gap-2 border-2 border-orange-400"
                  >
                    <XMarkIcon className="w-5 h-5" />
                    Cancel Donation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      <CancelModal />

      {/* Thank You Modal */}
      <ThankYouModal />
    </>
  );
};

export default DonationFormModal;